// ---------------------------------------------------------------------------
// Transform Aggregations to Chart Data
// ---------------------------------------------------------------------------
// Converts API aggregations directly to chart-ready format WITHOUT creating
// expanded arrays. Uses the COMPLETE universe of profiles/content/comments.
//
// REFACTOR NOTES:
// - Previously: Created expanded array of 10K individual objects
// - Now: Uses aggregations directly (542K profiles represented accurately)
// - Performance: O(n) where n = aggregation items, NOT total profiles
// - Memory: Minimal (no 10K+ object arrays)
// ---------------------------------------------------------------------------

/**
 * Aggregation item from API (name + count)
 */
export interface AggregationItem {
  readonly name: string
  readonly count: number
}

/**
 * Chart data item (label + value) - format expected by charts
 */
export interface ChartDataItem {
  readonly label: string
  readonly value: number
}

/**
 * Transform aggregation items to chart data items
 * Simple 1:1 mapping with renamed fields
 */
export function transformAggregationToChart(
  items: readonly AggregationItem[]
): readonly ChartDataItem[] {
  return items.map(item => ({
    label: item.name,
    value: item.count
  }))
}

/**
 * Extract the starting number from an age interval string
 * Handles formats with or without parentheses: "(0-3)" -> 0, "18-24" -> 18
 */
function getAgeIntervalStart(label: string): number {
  const match = label.match(/(\d+)/)
  return match ? parseInt(match[1], 10) : Infinity
}

/**
 * Sort age intervals by ascending age order
 */
function sortAgeIntervals(
  items: readonly ChartDataItem[]
): readonly ChartDataItem[] {
  return [...items].sort((a, b) =>
    getAgeIntervalStart(a.label) - getAgeIntervalStart(b.label)
  )
}

/**
 * Chart data structure for all dimensions
 * Each key maps to chart data for that dimension
 */
export interface ProfilesChartData {
  // Categorical distributions
  readonly platform: readonly ChartDataItem[]
  readonly gender: readonly ChartDataItem[]
  readonly age: readonly ChartDataItem[]
  readonly generation: readonly ChartDataItem[]
  readonly country: readonly ChartDataItem[]
  readonly ethnicity: readonly ChartDataItem[]
  readonly lifestyle: readonly ChartDataItem[]
  readonly statusClass: readonly ChartDataItem[]
  readonly industry: readonly ChartDataItem[]
  readonly city: readonly ChartDataItem[]
  readonly continent: readonly ChartDataItem[]
  readonly followerTier: readonly ChartDataItem[]

  // Boolean distributions
  readonly verified: readonly ChartDataItem[]
  readonly famous: readonly ChartDataItem[]
  readonly isPerson: readonly ChartDataItem[]
  readonly processed: readonly ChartDataItem[]

  // Count distributions
  readonly brands: readonly ChartDataItem[]
  readonly teams: readonly ChartDataItem[]

  // Sentiment/Emotion
  readonly sentiment: readonly ChartDataItem[]
  readonly dominantEmotion: readonly ChartDataItem[]
  readonly emotion: readonly ChartDataItem[]

  // Other
  readonly petFriendly: readonly ChartDataItem[]
  readonly createdAt: readonly ChartDataItem[]
  readonly authorCreatedAt: readonly ChartDataItem[]

  // Totals for display
  readonly totals: {
    readonly profiles: number
    readonly avgSentiment?: number
  }
}

/**
 * Transform Profiles API aggregations to chart data
 *
 * @param aggregations - Raw aggregations from /api/v11/discovery/profiles/aggregations
 * @returns Chart data for all dimensions (NO expanded array, NO 10K limit)
 */
export function transformProfilesAggregationsToChartData(
  aggregations: any
): ProfilesChartData | null {
  if (!aggregations?.data) return null

  const agg = aggregations.data

  return {
    platform: transformAggregationToChart(agg.platforms || []),
    gender: transformAggregationToChart(agg.gender || []),
    age: sortAgeIntervals(transformAggregationToChart(agg.age || [])),
    generation: transformAggregationToChart(agg.generation || []),
    country: transformAggregationToChart(agg.countries || []),
    ethnicity: transformAggregationToChart(agg.ethnicity || []),
    lifestyle: transformAggregationToChart(agg.lifestyle || []),
    statusClass: transformAggregationToChart(agg.statusClass || []),
    industry: transformAggregationToChart(agg.industry || []),
    city: transformAggregationToChart(agg.city || []),
    continent: transformAggregationToChart(agg.continent || []),
    followerTier: transformAggregationToChart(agg.followerTiers || []),

    verified: transformAggregationToChart(agg.verified || []),
    famous: transformAggregationToChart(agg.famous || []),
    isPerson: transformAggregationToChart(agg.isPerson || []),
    processed: transformAggregationToChart(agg.processed || []),

    brands: transformAggregationToChart(agg.brands || []),
    teams: transformAggregationToChart(agg.teams || []),

    sentiment: transformAggregationToChart(agg.sentiment || []),
    dominantEmotion: transformAggregationToChart(agg.dominantEmotion || []),
    // Emotion radar chart (use backend data directly, not hardcoded indices)
    emotion: agg.emotions
      ? agg.emotions.map((e: { name: string; count: number }) => ({
          label: e.name,
          value: e.count,
        }))
      : [],

    petFriendly: transformAggregationToChart(agg.petFriendly || []),
    createdAt: transformAggregationToChart(agg.createdAt || []),
    authorCreatedAt: transformAggregationToChart(agg.authorCreatedAt || []),

    totals: {
      profiles: agg.totals?.profiles || 0,
      avgSentiment: agg.totals?.avgSentiment || 0,
    }
  }
}

/**
 * Content chart data structure (similar to Profiles)
 */
export interface ContentChartData {
  readonly platform: readonly ChartDataItem[]
  readonly category: readonly ChartDataItem[]
  readonly mediaType: readonly ChartDataItem[]
  readonly isVideo: readonly ChartDataItem[]
  readonly engagement: readonly ChartDataItem[]
  readonly performance: readonly ChartDataItem[]
  readonly sentiment: readonly ChartDataItem[]
  readonly emotion: readonly ChartDataItem[]
  readonly authorCountry: readonly ChartDataItem[]
  readonly date: readonly ChartDataItem[]

  readonly totals: {
    readonly posts: number
    readonly avgEngagement: number
    readonly avgSentiment: number
  }
}

/**
 * Transform Content API aggregations to chart data
 */
export function transformContentAggregationsToChartData(
  aggregations: any
): ContentChartData | null {
  if (!aggregations?.data) return null

  const agg = aggregations.data

  return {
    platform: transformAggregationToChart(agg.platforms || []),
    category: transformAggregationToChart(agg.categories || []),
    mediaType: transformAggregationToChart(agg.mediaTypes || []),
    isVideo: transformAggregationToChart(agg.video || []),
    engagement: [],  // Gauge uses totals.avgEngagement, not distribution
    performance: transformAggregationToChart(agg.performance || []),
    sentiment: [],  // Gauge uses totals.avgSentiment, not distribution
    emotion: transformAggregationToChart(agg.emotion || []),
    authorCountry: transformAggregationToChart(agg.authorCountries || []),
    date: transformAggregationToChart(agg.dates || []),

    totals: {
      posts: agg.totals?.posts || 0,
      avgEngagement: agg.totals?.avgEngagement || 0,
      avgSentiment: agg.totals?.avgSentiment || 0,
    }
  }
}

/**
 * Comments chart data structure
 */
export interface CommentsChartData {
  readonly platform: readonly ChartDataItem[]
  readonly language: readonly ChartDataItem[]
  readonly sentiment: readonly ChartDataItem[]
  readonly emotion: readonly ChartDataItem[]
  readonly toxic: readonly ChartDataItem[]
  readonly spam: readonly ChartDataItem[]
  readonly author: readonly ChartDataItem[]
  readonly date: readonly ChartDataItem[]

  readonly totals: {
    readonly comments: number
    readonly avgSentiment: number
  }
}

/**
 * Transform Comments API aggregations to chart data
 */
export function transformCommentsAggregationsToChartData(
  aggregations: any
): CommentsChartData | null {
  if (!aggregations?.data) return null

  const agg = aggregations.data

  return {
    platform: transformAggregationToChart(agg.platforms || []),
    language: transformAggregationToChart(agg.languages || []),
    sentiment: transformAggregationToChart(agg.sentimentBuckets || agg.sentiment || []),
    emotion: transformAggregationToChart(agg.emotions || []),
    toxic: transformAggregationToChart(agg.toxic || []),
    spam: transformAggregationToChart(agg.spam || []),
    author: transformAggregationToChart(agg.authors || []),
    date: transformAggregationToChart(agg.dates || []),

    totals: {
      comments: agg.totals?.comments || 0,
      avgSentiment: agg.totals?.avgSentiment || 0,
    }
  }
}

/**
 * Brands chart data structure
 */
export interface BrandsChartData {
  readonly category: readonly ChartDataItem[]
  readonly subcategory: readonly ChartDataItem[]
  readonly name: readonly ChartDataItem[]
  readonly totalDetections: readonly ChartDataItem[]
  readonly profiles: readonly ChartDataItem[]

  readonly totals: {
    readonly brands: number
    readonly totalDetections: number
  }
}

/**
 * Transform Brands API aggregations to chart data
 */
export function transformBrandsAggregationsToChartData(
  aggregations: any
): BrandsChartData | null {
  if (!aggregations?.data) return null

  const agg = aggregations.data

  return {
    category: transformAggregationToChart(agg.categories || []),
    subcategory: transformAggregationToChart(agg.subcategories || []),
    name: transformAggregationToChart(agg.topBrands || []),
    totalDetections: transformAggregationToChart(agg.mentionTiers || []),
    profiles: transformAggregationToChart(agg.profileTiers || []),

    totals: {
      brands: agg.totals?.brands || 0,
      totalDetections: agg.totals?.totalDetections || 0,
    }
  }
}

/**
 * Teams chart data structure
 */
export interface TeamsChartData {
  readonly type: readonly ChartDataItem[]
  readonly country: readonly ChartDataItem[]
  readonly city: readonly ChartDataItem[]
  readonly name: readonly ChartDataItem[]
  readonly totalMentions: readonly ChartDataItem[]
  readonly commentMentions: readonly ChartDataItem[]
  readonly profiles: readonly ChartDataItem[]

  readonly totals: {
    readonly teams: number
    readonly totalMentions: number
  }
}

/**
 * Transform Teams API aggregations to chart data
 */
export function transformTeamsAggregationsToChartData(
  aggregations: any
): TeamsChartData | null {
  if (!aggregations?.data) return null

  const agg = aggregations.data

  return {
    type: transformAggregationToChart(agg.type || []),
    country: transformAggregationToChart(agg.countries || []),
    city: transformAggregationToChart(agg.cities || []),
    name: transformAggregationToChart(agg.topTeams || []),
    totalMentions: transformAggregationToChart(agg.mentionTiers || []),
    commentMentions: transformAggregationToChart(agg.commentMentionTiers || []),
    profiles: transformAggregationToChart(agg.profileTiers || []),

    totals: {
      teams: agg.totals?.teams || 0,
      totalMentions: agg.totals?.totalMentions || 0,
    }
  }
}
